import { getSectionDisplayNames, isSection } from '@tunasong/models'
import { Editor, EditorQueries, ElementApi } from '@tunasong/plugin-lib'
import type { NodeEntry, TunaEditor } from '@tunasong/plugin-lib'
import type { BlockRef, CoreElement } from '@tunasong/schemas'

/**
 * A section's heading as the editor shows it ("Verse 2"): repeated types are numbered in document order, the
 * same rule as the section header (`getSectionDisplayNames`). Walks the document's sections, so it runs only
 * when a section is in the stack.
 */
const sectionHeading = (editor: TunaEditor, sectionId: string): string | undefined => {
  const sections = Array.from(Editor.nodes(editor, { at: [], match: isSection })).map(([node]) => node)
  const index = sections.findIndex(section => section.id === sectionId)
  return index < 0 ? undefined : getSectionDisplayNames(sections.map(section => section.sectionType))[index]
}

/** Stable element id, if the node carries one. */
const elementId = (node: CoreElement): string | undefined => {
  const raw = (node as { id?: unknown }).id
  return typeof raw === 'string' ? raw : undefined
}

/** Section elements carry a `sectionType` ('Verse', 'Chorus', …) used for affordance gating. */
const sectionTypeOf = (node: CoreElement): string | undefined => {
  const raw = (node as { sectionType?: unknown }).sectionType
  return typeof raw === 'string' ? raw : undefined
}

const strProp = (node: CoreElement, key: string): string | undefined => {
  const raw = (node as Record<string, unknown>)[key]
  return typeof raw === 'string' ? raw : undefined
}

/** Human-readable chord name ('Cmaj7', 'G7/B') from a chord node's fields, for the focus chip. */
const chordSummary = (node: CoreElement): string | undefined => {
  if (node.type !== 'chord') {
    return undefined
  }
  const root = strProp(node, 'root')
  if (!root) {
    return undefined
  }
  const variant = strProp(node, 'variant') ?? ''
  const bassNote = strProp(node, 'bassNote')
  return `${root}${variant}${bassNote ? `/${bassNote}` : ''}`
}

const toBlockRef = (editor: TunaEditor, node: CoreElement, entityId: string): BlockRef | null => {
  const id = elementId(node)
  if (!id) {
    return null
  }
  const sectionType = sectionTypeOf(node)
  // `summary` is best-effort human context shown in the focus chip and sent to
  // the agent: the section's heading for sections ("Verse 2", so repeated
  // sections differ; Joni, #2503), the chord name for chords. `sectionType`
  // stays the typed gating source.
  const summary = node.type === 'section' ? (sectionHeading(editor, id) ?? sectionType) : chordSummary(node)
  return {
    id,
    type: node.type,
    entityId,
    ...(summary ? { summary } : {}),
    ...(sectionType ? { sectionType } : {}),
  }
}

/** Collects element refs, deduped by id, in the order added. */
const refCollector = (editor: TunaEditor, entityId: string) => {
  const refs = new Map<string, BlockRef>()
  const add = (node: unknown) => {
    if (!ElementApi.isElement(node)) {
      return
    }
    const ref = toBlockRef(editor, node as CoreElement, entityId)
    if (ref) {
      refs.set(ref.id, ref)
    }
  }
  // Walk leaf → root. Every prefix of a real node path is a valid node path,
  // so `nodeAt` never throws here; non-element ancestors (text leaves) are
  // ignored by `add`.
  const addAlong = (path: readonly number[]) => {
    for (let depth = path.length; depth >= 1; depth--) {
      const entry = EditorQueries.nodeAt(editor, path.slice(0, depth))
      if (entry) {
        add(entry[0])
      }
    }
  }
  return { add, addAlong, refs: () => Array.from(refs.values()) }
}

/**
 * The `BlockRef`s of the node at `path` and its ancestors, nearest-first: a
 * chord, its line, its section. For a block's own menu, which knows its node
 * but not the caret (GH #2503 review: the chord menu's ask said "About: this
 * line" inside a verse).
 */
export const blockRefsAt = (editor: TunaEditor, path: readonly number[], entityId: string): BlockRef[] => {
  const collector = refCollector(editor, entityId)
  collector.addAlong(path)
  return collector.refs()
}

/**
 * Collect the `BlockRef`s the editor caret currently sits within, nearest-first:
 * the inline chord (when the caret is on/inside the void chord), the line, and
 * the enclosing section. Walks the focus path's ancestor chain via
 * `EditorQueries.nodeAt` — pure path arithmetic, so it needs no Slate statics
 * and no `@tunasong/editor` dependency. Dedupes by element id.
 *
 * Chords are inline VOID elements: a caret typed *after* a chord lives in the
 * adjacent text node, so the chord is NOT an ancestor and won't appear here —
 * only a caret placed *on* the void (the click case) surfaces it. The per-chord
 * block-action menu covers the adjacent case, so the palette stays honest about
 * what the caret is actually on.
 */
export const cursorBlockRefs = (
  editor: TunaEditor,
  nodeEntry: NodeEntry<CoreElement> | undefined,
  entityId: string
): BlockRef[] => {
  const collector = refCollector(editor, entityId)
  const focusPath = editor.selection?.focus.path
  if (focusPath) {
    collector.addAlong(focusPath)
  }
  // `nodeEntry` is the command system's resolved node — include it in case it
  // differs from the focus-path walk (e.g., a selected void chord).
  if (nodeEntry) {
    collector.add(nodeEntry[0])
  }
  return collector.refs()
}
